import React, { useCallback, useEffect, useState } from "react";
import PaymentsAppLayout from "../../../Components/Molecules/PaymentsAppLayout";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import PrivateComponentLoadingPage from "../../LoadingPage/PrivateComponentLoadingPage";
import IntegrationCard from "../../../Components/Molecules/Settings/PaymentsIntegration/IntegrationCard";
import QuickbooksLogo from "../../../Assets/SettingsPages/IntegrationPage/QuickbooksLogo.svg";
import { ReactComponent as QuestionExclamation } from "../../../Assets/ActionIcons/QuestionExclamation.svg";
import XeroLogo from "../../../Assets/SettingsPages/IntegrationPage/XeroLogo.svg";
import { PaymentsIntegrationEnum } from "../../../Contracts/Integration";
import { useOnboardingStore } from "../../../Store/Onboarding";
import { connectIntegration } from "../../../Utils/IntegrationUtils";
import { useIntegrationStore } from "../../../Store/Integration";
import { useLocation, useNavigate } from "react-router-dom";
import { PaymentRoutes } from "../../../Contracts/Routes";

const PaymentsIntegrationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    getAdyenKYCDetails,
    adyenKYCDetails,
    integrationReAuthRequired,
    integrationReAuthMessage
  } = useOnboardingStore();
  const { integration, getConnectedIntegration, deleteIntegration } = useIntegrationStore();
  const { getAccessTokenSilently, user } = useAuth0();
  const refreshThings = useCallback(async () => {
    await Promise.all([
      getAdyenKYCDetails(getAccessTokenSilently),
      getConnectedIntegration(getAccessTokenSilently)
    ]);
  }, [getAdyenKYCDetails, getConnectedIntegration, getAccessTokenSilently]);
  useEffect(() => {
    refreshThings();
  }, [refreshThings]);

  const [showConnectedSuccessfullyConfirmation, setShowConnectedSuccessfullyConfirmation] =
    useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const connectedConfirmation = searchParams.get("oauth_completed") === "true";
    setShowConnectedSuccessfullyConfirmation(connectedConfirmation);
  }, [location.search]);

  const onIntegrationDisconnect = useCallback(async () => {
    const integrationId = integration?.id;
    if (integrationId) {
      await deleteIntegration(getAccessTokenSilently, integrationId);
      await getAdyenKYCDetails(getAccessTokenSilently);
    } else {
      throw new Error("Integration ID not found!");
    }
  }, [integration?.id, deleteIntegration, getAdyenKYCDetails, getAccessTokenSilently]);

  const onIntegrationConnect = useCallback(
    (integrationToConnect: PaymentsIntegrationEnum) => {
      connectIntegration(integrationToConnect, user?.sub);
    },
    [user?.sub]
  );

  const quickbooksConnected =
    adyenKYCDetails?.connected_integration === PaymentsIntegrationEnum.Quickbooks;
  const xeroConnected = adyenKYCDetails?.connected_integration === PaymentsIntegrationEnum.Xero;

  const onConnectionCompleteAcknowledged = useCallback(
    (connectedTo: PaymentsIntegrationEnum) => {
      setShowConnectedSuccessfullyConfirmation(false);
      if (connectedTo === PaymentsIntegrationEnum.Quickbooks) {
        navigate(PaymentRoutes.QUICKBOOKS_SETTINGS);
      } else if (connectedTo === PaymentsIntegrationEnum.Xero) {
        navigate(PaymentRoutes.XERO_SETTINGS);
      }
    },
    [setShowConnectedSuccessfullyConfirmation, navigate]
  );

  return (
    <PaymentsAppLayout>
      <div className="relative">
        {showConnectedSuccessfullyConfirmation && (
          <div className="z-40">
            <div className="fixed top-0 left-0 flex items-center justify-center w-screen h-[44rem] opacity-100 z-20">
              <div className="bg-white flex flex-col rounded shadow-lg max-w-[26rem] text-center border border-gray-200">
                <div className="font-semibold p-4">
                  {quickbooksConnected ? "QuickBooks" : "Xero"} Successfully Connected
                </div>
                <div className="text-center text-gray-500">
                  {quickbooksConnected ? "QuickBooks" : "Xero"} is now successfully connected and
                  your clients have been imported to Payments.
                </div>
                <div className="p-4 space-x-4 ml-auto">
                  <button
                    className="text-white px-4 py-1 rounded bg-gray-600 hover:bg-gray-400"
                    onClick={() =>
                      onConnectionCompleteAcknowledged(
                        quickbooksConnected
                          ? PaymentsIntegrationEnum.Quickbooks
                          : PaymentsIntegrationEnum.Xero
                      )
                    }
                  >
                    Ok
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="font-semibold mb-2">3RD PARTY INTEGRATIONS</div>
        <div className="mb-2 bg-gray-50 p-2 rounded">
          Integrate Zomentum with the applications you love
        </div>
        <div className="border-b border-gray-200 py-2 font-semibold">Accounting</div>
        <div className="flex gap-4 pt-8">
          {!xeroConnected && (
            <IntegrationCard
              integrationTitle="Quickbooks"
              integrationDescription="Connect QuickBooks Online with Zomentum Payments to sync your clients, auto-collect invoices and record payments and fees."
              connected={quickbooksConnected}
              logoSvg={QuickbooksLogo}
              onConnect={() => onIntegrationConnect(PaymentsIntegrationEnum.Quickbooks)}
              disconnectConfirmationTitle={
                <div className="flex space-x-3">
                  <QuestionExclamation className="text-orange-500" />
                  <div className="text-lg font-semibold text-left">Reconnecting Accounts</div>
                </div>
              }
              disconnectConfirmationDescription={
                <div className="text-left text-gray-500 flex flex-col space-y-2">
                  <p>
                    Please be aware that if you reconnect to a different QuickBooks Online account,
                    you could end up with duplicate customer entries.{" "}
                  </p>
                  <p>
                    {" "}
                    If you have migrated to a new account, or have merged multiple accounts into
                    one, please{" "}
                    <a
                      className="text-blue-600"
                      href="mailto:support@zomentum.com?subject=Connecting a new integration account in Payments"
                    >
                      contact support
                    </a>{" "}
                    so we can help migrate your data.
                  </p>
                  <p>Are you sure you want to continue?</p>
                </div>
              }
              onDisconnect={onIntegrationDisconnect}
              editRoute={PaymentRoutes.QUICKBOOKS_SETTINGS}
            />
          )}
          {!quickbooksConnected && (
            <div>
              <IntegrationCard
                integrationTitle="Xero"
                integrationDescription="Connect Xero with Zomentum Payments to sync your clients, auto-collect invoices and record payments and fees."
                connected={xeroConnected}
                logoSvg={XeroLogo}
                disconnectConfirmationDescription={
                  <div className="text-left text-gray-500 flex flex-col space-y-2">
                    <p>
                      Please be aware that if you reconnect to a different Xero account, you could
                      end up with duplicate customer entries.{" "}
                    </p>
                    <p>
                      {" "}
                      If you have migrated to a new account, or have merged multiple accounts into
                      one, please{" "}
                      <a
                        className="text-blue-600"
                        href="mailto:support@zomentum.com?subject=Connecting a new integration account in Payments"
                      >
                        contact support
                      </a>{" "}
                      so we can help migrate your data.
                    </p>
                    <p>Are you sure you want to continue?</p>
                  </div>
                }
                disconnectConfirmationTitle={
                  <div className="flex space-x-3">
                    <QuestionExclamation className="text-orange-500" />
                    <div className="text-lg font-semibold text-left">Reconnecting Accounts</div>
                  </div>
                }
                editRoute={PaymentRoutes.XERO_SETTINGS}
                onConnect={() => onIntegrationConnect(PaymentsIntegrationEnum.Xero)}
                onDisconnect={onIntegrationDisconnect}
              />
              {integrationReAuthRequired &&
                adyenKYCDetails?.connected_integration === PaymentsIntegrationEnum.Xero && (
                  <div className="w-full bg-yellow-100 p-3 my-2 flex flex-col items-center space-y-2">
                    <div className="flex items-center space-x-2">
                      <div>
                        {integrationReAuthMessage ||
                          "Sync paused. Please reconnect your Xero account."}
                      </div>
                    </div>
                    <button
                      className="bg-blue-600 text-white px-2 py-1 rounded w-fit"
                      onClick={() => onIntegrationConnect(PaymentsIntegrationEnum.Xero)}
                    >
                      Reauthorize
                    </button>
                  </div>
                )}
            </div>
          )}
        </div>
      </div>
    </PaymentsAppLayout>
  );
};

export default withAuthenticationRequired(PaymentsIntegrationPage, {
  onRedirecting: () => <PrivateComponentLoadingPage />
});
